import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Input from "components/input";
import {
  DTP_SERVICE_NAME,
  INTERPRETATION_SERVICE_NAME,
  LQE_REVIEW_SERVICE_NAME,
  LANGUAGE_MODEL_NONE,
} from "utils/constantValues";

const RateTypesInputs = ({
  rateType,
  register,
  onHandleChange,
  languagePairKey,
  langPairIndex,
  serviceId,
  rateIndex,
  setValue,
  showDisclaimer,
  isCharacter = false,
  serviceRatesPerLanPair,
}) => {
  const [t] = useTranslation();

  const isInterpretation = rateType.serviceName === INTERPRETATION_SERVICE_NAME;
  const isDtp = rateType.serviceName === DTP_SERVICE_NAME;

  const hasValidRate = Array.isArray(serviceRatesPerLanPair)
    ? serviceRatesPerLanPair.some(
        (item) => parseFloat(item.rate) > 0 && !isNaN(parseFloat(item.rate))
      )
    : false;

  // DTP dynamic required field logic
  const getDtpRequiredLogic = () => {
    if (!isDtp || !Array.isArray(serviceRatesPerLanPair)) {
      return { isRequired: false, hasAnyFilledRate: false };
    }

    // Check if any rate type has filled rate or capacity
    const filledRateTypes = serviceRatesPerLanPair.filter(
      (item) =>
        (parseFloat(item.rate) > 0 && !isNaN(parseFloat(item.rate))) ||
        (parseFloat(item.capacity) > 0 && !isNaN(parseFloat(item.capacity)))
    );

    const hasAnyFilledRate = filledRateTypes.length > 0;

    // If no rates are filled, all fields are required (initial state)
    if (!hasAnyFilledRate) {
      return { isRequired: true, hasAnyFilledRate: false };
    }

    // If some rates are filled, only check if current rate type is filled
    const currentRateTypeFilled =
      (parseFloat(rateType.rate) > 0 && !isNaN(parseFloat(rateType.rate))) ||
      (parseFloat(rateType.capacity) > 0 &&
        !isNaN(parseFloat(rateType.capacity)));

    return {
      isRequired: currentRateTypeFilled,
      hasAnyFilledRate: true,
    };
  };

  const dtpLogic = getDtpRequiredLogic();

  // Disable rate/capacity until languages are selected (but enable for NONE model)
  const isNoneModel = rateType.languageModel === LANGUAGE_MODEL_NONE;
  const isPairModel = !!rateType.isPair;
  const isLangReady = isNoneModel
    ? true
    : isPairModel
    ? Boolean(rateType.fromLanguage) && Boolean(rateType.toLanguage)
    : Boolean(rateType.toLanguage);

  const showError = (isRate) => {
    const rateString = isRate
      ? String(rateType.rate)
      : String(rateType.capacity);

    const decimalPart = rateString.split(".")[1] || "";

    if (isInterpretation) {
      if (hasValidRate) {
        return { isValid: true, message: "" };
      }
      return { isValid: false, message: "atLeastOneRateRequired" };
    }

    // DTP validation logic
    if (isDtp) {
      // If this field is required by DTP logic and is empty/zero, show error
      if (
        dtpLogic.isRequired &&
        (!rateString || parseFloat(rateString) === 0)
      ) {
        return { isValid: false, message: "emptyOrZero" };
      }
      // If field has value, validate it normally
      if (rateString && parseFloat(rateString) > 0) {
        if (isRate && decimalPart.length > 3) {
          return { isValid: false, message: "rateValueError" };
        }
        if (
          !isRate &&
          rateType?.CsLinguistSupportedServices &&
          !Number.isInteger(parseFloat(rateString))
        ) {
          return { isValid: false, message: "mustBeInteger" };
        }
      }
      return { isValid: true, message: "" };
    }

    if (!rateString || parseFloat(rateString) === 0) {
      return { isValid: false, message: "emptyOrZero" };
    }

    if (isRate && decimalPart.length > 3) {
      return { isValid: false, message: "rateValueError" };
    }

    if (
      !isRate &&
      rateType?.CsLinguistSupportedServices &&
      !Number.isInteger(parseFloat(rateString))
    ) {
      return { isValid: false, message: "mustBeInteger" };
    }

    return { isValid: true, message: "" };
  };

  const validationCapacityResult = showError(false);

  const validationRateResult = showError(true);
  const errorMessageForRates = !validationRateResult.isValid
    ? t(validationRateResult.message)
    : "";

  return (
    <div className="grid gap-[10px] w-full">
      {rateType.withRate && (
        <div
          className={`grid ${
            rateType.withCapacity ? "grid-cols-2" : "grid-cols-1"
          } sm-max:grid-cols-1 gap-[10px]`}
        >
          <div className="flex flex-col gap-[16px]">
            {rateType.withRate && (
              <div className="text-primary font-medium text-[16px] not-italic mt-[10px] ">
                {isInterpretation
                  ? t(`subTitle-${rateType.serviceName}-${rateType.title}`)
                  : t(`subTitle-${rateType.title}`, {
                      isCharacter: isCharacter ? t("character") : t("word"),
                    })}
              </div>
            )}

            <Input
              value={rateType.rate || null}
              placeholder={t(`rate-${rateType.title}`, {
                isCharacter: isCharacter ? t("character") : t("word"),
              })}
              onChange={(e) => {
                const newRate = e.target.value;
                setValue(
                  `rate-${serviceId}-${languagePairKey}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`,
                  newRate
                );

                onHandleChange(
                  newRate,
                  serviceId,
                  languagePairKey,
                  "rate",
                  rateType.rateId,
                  setValue
                );
              }}
              label=""
              inputMode="numeric"
              type="number"
              name={`rate-${serviceId}-${languagePairKey}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`}
              register={register}
              registerArgs={{
                required: (() => {
                  if (isInterpretation && !hasValidRate) {
                    return t("errorField", { field: t("rate") });
                  }
                  if (isDtp && dtpLogic.isRequired) {
                    return t("errorField", { field: t("rate") });
                  }
                  return false;
                })(),
                validate: () => {
                  if (!isLangReady) return true;
                  return validationRateResult.isValid
                    ? true
                    : t(validationRateResult.message);
                },
              }}
              min={0}
              step={0.001}
              isDisabled={!isLangReady}
              labelClassName="text-primary font-semibold text-[16px]"
              inputClassName={`flex h-[43px] w-full sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                validationRateResult.isValid || !isLangReady
                  ? "border-default-color"
                  : "border-red"
              } ${
                !isLangReady
                  ? "text-[#D1D1D6] bg-[#F4F4F5] cursor-not-allowed"
                  : "bg-transparent text-dark-gray placeholder:text-gray-500"
              }  outline-none  font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal  py-[16px] px-[14px]`}
              isRequired={!validationRateResult.isValid && isLangReady}
              errorMessage={
                isLangReady && (!isInterpretation || isDtp)
                  ? errorMessageForRates
                  : ""
              }
            />
            {rateType.withRate && showDisclaimer && (
              <div className="text-red font-bold text-[14px] not-italic mt-[10px] ">
                {t("rateDisclaimer")}
              </div>
            )}
          </div>

          {rateType.withCapacity && (
            <div className="flex flex-col gap-[16px]">
              {rateType.withCapacity && (
                <div className="text-primary font-medium text-[16px] not-italic mt-[10px] ">
                  {rateType.serviceName === LQE_REVIEW_SERVICE_NAME
                    ? t(`Capacity-${rateType.serviceName}-${rateType.title}`, { defaultValue: "" }) || t(`Capacity-${rateType.title}`, {
                        isCharacter: isCharacter ? t("character") : t("word"),
                      })
                    : t(`Capacity-${rateType.title}`, {
                        isCharacter: isCharacter ? t("character") : t("word"),
                      })}
                </div>
              )}
              <Input
                value={rateType.capacity || null}
                placeholder={
                  rateType.serviceName === DTP_SERVICE_NAME
                    ? t(`${rateType.serviceName}-${rateIndex}`)
                    : t(`${rateType.serviceName}-${rateType.title}`, { defaultValue: "" }) ||
                      t(`${rateType.title}`, {
                        isCharacter: isCharacter
                          ? t("character").toLowerCase()
                          : t("word"),
                      })
                }
                min={0}
                onChange={(e) => {
                  const newCapacity = e.target.value;

                  setValue(
                    `capacity-${serviceId}-${languagePairKey}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`,
                    newCapacity
                  );

                  onHandleChange(
                    newCapacity,
                    serviceId,
                    languagePairKey,
                    "capacity",
                    rateType.rateId,
                    setValue
                  );
                }}
                register={register}
                registerArgs={{
                  required: (() => {
                    if (isInterpretation && !hasValidRate) {
                      return t("errorField", { field: t("capacity") });
                    }
                    if (isDtp && dtpLogic.isRequired) {
                      return t("errorField", { field: t("capacity") });
                    }
                    return false;
                  })(),
                  validate: () => {
                    if (!isLangReady) return true;
                    return validationCapacityResult.isValid
                      ? true
                      : t(validationCapacityResult.message);
                  },
                }}
                label=""
                inputMode="numeric"
                type="number"
                isDisabled={!isLangReady}
                name={`capacity-${serviceId}-${languagePairKey}-${rateIndex}-${rateType.rateId}-${rateType.fromLanguage}-${rateType.toLanguage}`}
                labelClassName="text-primary font-semibold text-[16px]"
                inputClassName={`flex h-[43px] w-full sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
                  validationCapacityResult.isValid || !isLangReady
                    ? "border-default-color"
                    : "border-red"
                }  outline-none ${
                  !isLangReady
                    ? "text-[#D1D1D6] bg-[#F4F4F5] cursor-not-allowed"
                    : "text-dark-gray bg-transparent"
                } font-regular text-[16px] not-italic  placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px] `}
                isRequired={!validationCapacityResult.isValid && isLangReady}
                errorMessage={
                  isLangReady && (!isInterpretation || isDtp)
                    ? t(validationCapacityResult.message)
                    : ""
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

RateTypesInputs.propTypes = {
  rateType: PropTypes.object,
  onHandleChange: PropTypes.func,
  register: PropTypes.func,
  serviceId: PropTypes.string,
  rateIndex: PropTypes.number,
  setValue: PropTypes.func,
  showDisclaimer: PropTypes.bool,
  isCharacter: PropTypes.bool,
};

export default RateTypesInputs;
